import { FunctionComponent, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";

import {
	useGetCourseVersionsQuery,
	usePublishCourseDraftMutation,
	usePublishCourseVersionMutation,
	useUnpublishCourseMutation,
} from "@/pages/Private/redux/courses/courses.api";
import { isUnreachableError, readApiErrorMessage, readScenarioIssues } from "@/utils/apiError";
import { classNames } from "@/utils/classNames";

import { CourseStatus } from "../../enums/CourseStatus.enum";

interface PublishStatusProps {
	status?: CourseStatus;
}

/**
 * Whether this scenario is live.
 *
 * Only that. Whether anything is *waiting* to go live is said in the editor's own
 * unsaved/unpublished slot, where the author is already looking for the state of their
 * work — on one bar, saying it twice made the two badges read as different facts.
 */
export const PublishStatus: FunctionComponent<PublishStatusProps> = ({ status }) => {
	const { t } = useTranslation();
	const isActive = status === CourseStatus.Active;

	return (
		<span
			className={classNames(
				"rounded-full px-3 py-1 text-xs font-bold",
				isActive ? "bg-success-light text-text" : "bg-surface text-subtle"
			)}
		>
			{t(`editor.publish.${isActive ? "statusActive" : "statusDraft"}`)}
		</span>
	);
};

interface PublishControls {
	/** True once a save has written a draft. */
	hasDraft: boolean;
	/**
	 * Whether pressing publish would do anything: a draft to put live, or — for a scenario
	 * that was withdrawn and has not been edited since — the version it was showing before.
	 */
	canPublish: boolean;
	isActive: boolean;
	isBusy: boolean;
	isPublishing: boolean;
	/** The server's refusal, or an unreachable-server note, for the caller to place. */
	error: string;
	/** Which version is live and which one the editor is on — the indicator's tooltip. */
	editingHint: string;
	askPublish: () => void;
	askUnpublish: () => void;
	/** Both confirm dialogs; rendered once, anywhere. */
	dialogs: ReactNode;
}

/**
 * Publishing and withdrawing, as state rather than as a component — BCM360-139.
 *
 * A hook because the two buttons no longer sit together: publishing belongs beside Save
 * and withdrawing beside Cancel, and one component rendering both could only ever put them
 * next to each other. What they still share — a pair of confirm dialogs, one request at a
 * time, one error line — is what this keeps in one place.
 */
export const usePublishControls = (
	courseId: number,
	status: CourseStatus | undefined,
	publishedVersionId: number | null | undefined,
	onChanged: () => void
): PublishControls => {
	const { t } = useTranslation();
	// Rendered as React text, which escapes it already. i18next's own escaping on top turned
	// an apostrophe in a nested line ("this phase's options") into "&#39;" (BCM360-364).
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`editor.publish.${key}`, { ...options, interpolation: { escapeValue: false } }),
		[t]
	);

	const { data: versions } = useGetCourseVersionsQuery(courseId);
	const [publishDraft, { isLoading: isPublishingDraft }] = usePublishCourseDraftMutation();
	const [publishVersion, { isLoading: isRepublishing }] = usePublishCourseVersionMutation();
	const [unpublish, { isLoading: isUnpublishing }] = useUnpublishCourseMutation();

	const [confirming, setConfirming] = useState<"publish" | "unpublish" | null>(null);
	const [error, setError] = useState("");

	const isActive = status === CourseStatus.Active;
	const hasDraft = (versions ?? []).some((version) => version.isDraft);
	const isPublishing = isPublishingDraft || isRepublishing;
	const isBusy = isPublishing || isUnpublishing;

	// Withdrawing consumes nothing and creates nothing: the version that was live is still
	// there and still pointed at. Publishing it again is how a hidden scenario comes back,
	// and without this the button that says so was dead in exactly that state.
	const canRepublish = !isActive && !hasDraft && !!publishedVersionId;
	const canPublish = hasDraft || canRepublish;

	// Which version is live, and which one this editor is on. They part company the moment
	// anything is saved, and that difference is the whole reason the indicator exists.
	const published = (versions ?? []).find((version) => version.id === publishedVersionId);
	const publishedName = published
		? published.label || ts("unnamedVersion", { id: published.id })
		: ts("nothingLive");
	// Both halves named: which version is live, and which one this editor is in. Naming
	// only the first leaves "and what am I looking at, then?" unanswered.
	const editingHint = hasDraft
		? ts("editingDraft", { published: publishedName, editing: ts("draftName") })
		: ts("editingLive", { published: publishedName });

	const run = async (action: "publish" | "unpublish") => {
		setError("");

		try {
			const request =
				action === "unpublish"
					? unpublish(courseId)
					: hasDraft
						? publishDraft(courseId)
						: publishVersion({ id: courseId, versionId: publishedVersionId as number });

			await request.unwrap();
			setConfirming(null);
			onChanged();
		} catch (caught) {
			setConfirming(null);
			// Which phase is incomplete is the only part that tells the author what to fix,
			// so it is said in their language rather than the server's. A request that never
			// arrived is a different problem with different advice.
			const issues = readScenarioIssues(caught);

			setError(
				isUnreachableError(caught)
					? ts("unreachable")
					: issues
						? ts("incomplete", {
								issues: issues
									.map((issue) => {
										const where = issue.phase
											? ts("issuePhase", {
													number: issue.phase,
													name: issue.phaseName ? ` „${issue.phaseName}“` : "",
												})
											: ts("issueScenario");
										const fields = issue.fields
											.map((field) => t(`editor.publish.field.${field}`, field))
											.join(", ");
										// Where the flow is broken (BCM360-364): a missing field
										// says what to fill in, a problem says what to reconnect.
										const problems = (issue.problems ?? []).map((problem) =>
											ts(`problem.${problem.code}`, {
												target: problem.target,
												phases: problem.phases?.join(", "),
											})
										);

										return [
											...(fields.length ? [ts("issueLine", { where, fields })] : []),
											...problems.map((problem) => `${where}: ${problem}`),
										].join("; ");
									})
									.join("; "),
							})
						: readApiErrorMessage(caught) || ts(`${action}Failed`)
			);
		}
	};

	return {
		hasDraft,
		canPublish,
		isActive,
		isBusy,
		isPublishing,
		error,
		editingHint,
		askPublish: () => setConfirming("publish"),
		askUnpublish: () => setConfirming("unpublish"),
		dialogs: (
			<>
				<Modal
					handleClose={() => setConfirming(null)}
					handleSave={() => run("publish")}
					isOpened={confirming === "publish"}
					size="xs"
					submitButtonText={ts("publishConfirm")}
					title={ts("publishTitle")}
				>
					<p className="text-sm text-text">
						{canRepublish
							? ts("republishWarning")
							: isActive
								? ts("publishWarning")
								: ts("publishFirstWarning")}
					</p>
				</Modal>

				<Modal
					handleClose={() => setConfirming(null)}
					handleSave={() => run("unpublish")}
					isOpened={confirming === "unpublish"}
					size="xs"
					submitButtonText={ts("unpublishConfirm")}
					title={ts("unpublishTitle")}
				>
					<p className="text-sm text-text">{ts("unpublishWarning")}</p>
				</Modal>
			</>
		),
	};
};
